import { z } from 'zod'

import { dateUtil, isNull, isPresent, isSome } from '../../util'

type ParseCsvLengthResult = { success: true; lengthMm: number | null } | { success: false; message: string }

/**
 * CSV取込用: 長さ文字列をmm整数値に厳密変換する
 *
 * normalizeSteelLengthToMm とは異なり、ヒューリスティック推定・範囲チェックは行わない。
 * 単純な単位変換と整数性チェックのみ。不正な値はエラーを返す。
 */
export function parseCsvLengthToMm(value: string | null | undefined, unit: 'm' | 'mm'): ParseCsvLengthResult {
  if (!isPresent(value)) {
    return { success: true, lengthMm: null }
  }

  const trimmed = value.trim()
  if (trimmed === '') {
    return { success: true, lengthMm: null }
  }

  const numericValue = Number(trimmed)

  if (isNaN(numericValue)) {
    return { success: false, message: `数値として解釈できません（入力値: ${value}）` }
  }

  if (numericValue < 0) {
    return { success: false, message: `正の数値で入力してください（入力値: ${value}）` }
  }

  const lengthMmRaw = unit === 'mm' ? numericValue : numericValue * 1000
  const lengthMm = Math.round(lengthMmRaw)

  if (Math.abs(lengthMmRaw - lengthMm) > 0.01) {
    const requirement = unit === 'mm' ? '整数値' : '0.001刻みの値'
    return {
      success: false,
      message: `長さは${requirement}で入力してください。単位: ${unit}（入力値: ${value}）`,
    }
  }

  return { success: true, lengthMm }
}

export const millsheetCsvColumnMapper = {
  manufacturerName: 'メーカー名',
  heatNumber: '製鋼番号',
  no: '発行No.',
  quantity: '員数',
  issuedDate: '発行日',
  steelType: '種類（記号）',
  size: '断面形状',
  length: '長さ',
  weight: '備考',
  constructionName: '工事名称',
  deliveryDestination: '納入先',
  notificationNo: '通知番号',
}

export function validateAndNormalizeMillsheetCsvRecords({
  record,
  index,
  lengthUnit,
}: {
  record: Record<string, string | number | null>
  index: number
  lengthUnit?: 'm' | 'mm'
}) {
  // すべて空行だったらスキップ
  if (
    Object.values(record).every((x) => x === '')
  ) {
    return {
      type: 'rebar' as const,
      success: false as const,
      isEmptyLine: true,
      message: `空行です。`,
      record,
      index,
    }
  }

  // TODO: keysのvalidation

  // 必要なデータのみを一旦抽出する
  const data = {
    manufacturerName: record[millsheetCsvColumnMapper.manufacturerName],
    heatNumber: record[millsheetCsvColumnMapper.heatNumber],
    no: record[millsheetCsvColumnMapper.no],
    quantity: record[millsheetCsvColumnMapper.quantity],
    issuedDate: record[millsheetCsvColumnMapper.issuedDate],
    steelType: record[millsheetCsvColumnMapper.steelType],
    size: record[millsheetCsvColumnMapper.size],
    weight: record[millsheetCsvColumnMapper.weight],
    length: record[millsheetCsvColumnMapper.length],
    constructionName: record[millsheetCsvColumnMapper.constructionName],
    deliveryDestination: record[millsheetCsvColumnMapper.deliveryDestination],
    notificationNo: record[millsheetCsvColumnMapper.notificationNo],
  }
  // schemaもハードコーディング
  const schema = z.object({
    manufacturerName: z.string(),
    heatNumber: z.string(),
    no: z.string(),
    // quantityは数値の文字列（カンマ、小数点を許す）
    quantity: z
      .string()
      .regex(/^[0-9,.]+$/, { message: '数値で入力してください。' })
      .transform((x) => {
        return Number(x.replace(/,/g, ''))
      }),
    issuedDate: z.string(),
    steelType: z.string(),
    size: z.string(),
    weight: z
      .string()
      .regex(/^[0-9,.]+$/, { message: '数値で入力してください。' })
      .transform((x) => {
        return Number(x.replace(/,/g, ''))
      }),
    length: z.string().optional(),
    constructionName: z.string(),
    deliveryDestination: z.string(),
    notificationNo: z.string().nullish(),
  })
  const parsed = schema.safeParse(data)

  if (!parsed.success) {
    return {
      type: 'rebar' as const,
      success: false as const,
      message: parsed.error.issues
        .map((x) => {
          // ネストや refinement 由来の path は mapper のキーに存在しないため、パス文字列にフォールバックする
          const columnLabels: Partial<Record<string, string>> = millsheetCsvColumnMapper
          const label = columnLabels[x.path.join('')] ?? x.path.join('')
          const value = data[x.path.join('') as keyof typeof data]
          return `${label}: ${x.message}(入力された値: ${value})`
        })
        .join('。'),
      record,
      index,
    }
  }

  const dateParsed = dateUtil.parseDateFormatString(parsed.data.issuedDate)
  if (isNull(dateParsed)) {
    return {
      type: 'rebar' as const,
      success: false as const,
      message: `日付形式がYYYY/MM/DDの形式になっていません。入力：${parsed.data.issuedDate}`,
      record,
      index,
    }
  }

  const parsedLength = isSome(lengthUnit)
    ? parseCsvLengthToMm(parsed.data.length, lengthUnit)
    : ({ success: true, lengthMm: null } as const)

  if (!parsedLength.success) {
    return {
      type: 'rebar' as const,
      success: false as const,
      message: `${millsheetCsvColumnMapper.length}: ${parsedLength.message}`,
      record,
      index,
    }
  }

  const result = {
    type: 'rebar' as const,
    ...parsed.data,
    rank: index + 1,
    issuedDate: dateParsed.date,
    lengthMm: parsedLength.lengthMm,
  }

  return {
    type: 'rebar' as const,
    success: true as const,
    record,
    index,
    result,
    resultTitle: millsheetCsvColumnMapper.heatNumber,
  }
}
